'use client';

import { Calendar, Clock } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import type { DCMAScoreResult } from '@/lib/dcma-scoring-engine';

interface ScoreOverviewProps {
  result: DCMAScoreResult;
}

function getGradeVar(grade: string): string {
  switch (grade) {
    case 'A':
      return 'var(--grade-a)';
    case 'B':
      return 'var(--grade-b)';
    case 'C':
      return 'var(--grade-c)';
    case 'D':
      return 'var(--grade-d)';
    case 'F':
      return 'var(--grade-f)';
    default:
      return 'var(--muted-foreground)';
  }
}

function getBarColorVar(score: number): string {
  if (score >= 90) return 'var(--grade-a)';
  if (score >= 80) return 'var(--grade-b)';
  if (score >= 70) return 'var(--grade-c)';
  if (score >= 60) return 'var(--grade-d)';
  return 'var(--grade-f)';
}

function formatDate(dateStr: string): string {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// Tally up deductions by category
function getCategoryDeductions(result: DCMAScoreResult): { category: string; deduction: number }[] {
  const map = new Map<string, number>();
  for (const cr of result.criterionResults) {
    if (!cr.enabled || cr.scoreImpact >= 0) continue;
    const existing = map.get(cr.categoryLabel) || 0;
    map.set(cr.categoryLabel, existing + cr.scoreImpact);
  }
  return Array.from(map.entries()).map(([category, deduction]) => ({ category, deduction }));
}

export function ScoreOverview({ result }: ScoreOverviewProps) {
  const gradeColor = getGradeVar(result.grade);
  const categoryDeductions = getCategoryDeductions(result);
  const totalDeductions = categoryDeductions.reduce((sum, d) => sum + d.deduction, 0);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
          {/* Score circle */}
          <div className="flex flex-col items-center gap-3">
            <div
              className="flex h-28 w-28 flex-col items-center justify-center rounded-full ring-4"
              style={
                {
                  backgroundColor: `oklch(from ${gradeColor} l c h / 0.1)`,
                  '--tw-ring-color': `oklch(from ${gradeColor} l c h / 0.3)`,
                } as React.CSSProperties
              }
            >
              <span
                className="text-3xl font-bold"
                style={{ color: gradeColor }}
              >
                {result.totalScore}
              </span>
              <span className="text-xs text-muted-foreground">/ 100</span>
            </div>
            <Badge
              variant="outline"
              className="text-lg font-bold px-4 py-1"
              style={{ color: gradeColor, borderColor: gradeColor }}
            >
              Grade {result.grade}
            </Badge>
          </div>

          {/* Score details */}
          <div className="flex-1 space-y-4">
            {/* Score bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground">Overall DCMA Score</span>
                <span className="text-muted-foreground">{result.totalScore} / 100</span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${result.totalScore}%`,
                    backgroundColor: getBarColorVar(result.totalScore),
                  }}
                />
              </div>
            </div>

            {/* Deduction summary */}
            {totalDeductions < 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    Deductions by Category
                  </p>
                  <span className="text-xs text-destructive font-semibold">
                    ({totalDeductions.toFixed(1)} pts total)
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {categoryDeductions.map(({ category, deduction }) => (
                    <div
                      key={category}
                      className="flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1"
                    >
                      <span className="text-xs text-muted-foreground">{category}</span>
                      <span className="text-xs font-semibold text-destructive">
                        {deduction.toFixed(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Project dates */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Data Date:</span>
                <span className="text-xs font-medium text-foreground">
                  {formatDate(result.dataDate)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Start:</span>
                <span className="text-xs font-medium text-foreground">
                  {formatDate(result.plannedStart)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Finish:</span>
                <span className="text-xs font-medium text-foreground">
                  {formatDate(result.scheduledFinish)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
